import React, { useEffect } from 'react';
import { useAppSelector } from 'hooks';
import { Input, Label } from 'reactstrap';
import dayjs from 'dayjs';
import { defaultMinuteInterval, formatDateTimeValue, getTime, timeValueFormat } from 'tsx/libs/dayjs';

import ClickableIcon from '~components/ClickableIcon';
import { InputProps } from '~components/form-fields';
import * as FormInput from '~components/form-fields/inputs';
import { faAngleLeft, faAngleRight } from '~components/icon';

import { selectCompanySettings } from '~main/selectors/login';

function FormInputStartEndTimes({ value, onChange, disabled }: InputProps) {
  let { start_time, end_time, ends_next_day } = value;

  let minutes = defaultMinuteInterval;
  const settings = useAppSelector(selectCompanySettings);
  if (settings?.SYSTEM_2022_IntervalHops) minutes = parseInt(settings.SYSTEM_2022_IntervalHops as string);

  start_time = formatDateTimeValue('time', start_time);
  end_time = formatDateTimeValue('time', end_time);
  ends_next_day = formatDateTimeValue('checkbox', ends_next_day);

  useEffect(() => {
    if (start_time && !end_time) {
      const newEndTime = getTime(start_time, minutes);
      onChange && onChange('end_time', dayjs(newEndTime).format(timeValueFormat));
    }
  }, [start_time, end_time, minutes]);

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    onChange && onChange(name, checked);
  };

  const isQuickMoveDisabled = (!start_time && !end_time) || disabled;

  const handleQuickTimeShift = (addTime: boolean) => {
    if (isQuickMoveDisabled) return;

    const timeShift = addTime ? minutes : -minutes;
    const newStartTime = getTime(start_time, timeShift);
    const newEndTime = getTime(end_time, timeShift);

    onChange && onChange('start_time', dayjs(newStartTime).format(timeValueFormat));
    onChange && onChange('end_time', dayjs(newEndTime).format(timeValueFormat));
  };

  return (
    <div className="d-flex flex-wrap align-items-center">
      <div className="d-flex align-items-center me-2 mb-2">
        <FormInput.Time
          id="start_time"
          name="start_time"
          type="time"
          value={start_time ?? ''}
          disabled={disabled}
          placeholder="Start"
          onChange={onChange}
          dataCy="start_time"
        />
        <div className="ms-2 me-2">to</div>
        <FormInput.Time
          id="end_time"
          name="end_time"
          type="time"
          value={end_time ?? ''}
          disabled={disabled}
          placeholder="End"
          onChange={onChange}
          dataCy="end_time"
        />
        <ClickableIcon
          id="move_time_earlier_icon"
          icon={faAngleLeft}
          className="ms-2"
          disabled={isQuickMoveDisabled}
          onClick={() => handleQuickTimeShift(false)}
          dataCy="move-time-earlier-icon"
          toolTip="Move time earlier"
        />
        <ClickableIcon
          id="move_time_later_icon"
          icon={faAngleRight}
          className="me-2"
          disabled={isQuickMoveDisabled}
          onClick={() => handleQuickTimeShift(true)}
          dataCy="move-time-later-icon"
          toolTip="Move time later"
        />
      </div>
      <div className="d-flex align-items-center justify-content-center w-auto mb-2">
        <Input
          id="ends_next_day"
          type="checkbox"
          name="ends_next_day"
          checked={ends_next_day ?? false}
          onChange={handleCheckboxChange}
          data-cy="ends_next_day-input"
          className="mb-1"
        />
        <Label for="ends_next_day" className="ms-2 mb-0">
          Overnight
        </Label>
      </div>
    </div>
  );
}

export default FormInputStartEndTimes;
